import React, { useContext, useState } from 'react';
import { ShopContext } from '../../Context/ShopContext';
import { Link, useNavigate } from 'react-router-dom';
import './ProductDisplay.css';

const ProductDisplay = ({ product }) => {
  const {
    addToCart,
    cartItems,
    showToast,
    checkAuthBeforeCartAction
  } = useContext(ShopContext);
  const [quantity, setQuantity] = useState(1);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [sizeError, setSizeError] = useState(false);
  const [colorError, setColorError] = useState(false);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [activeImage, setActiveImage] = useState(0);
  const navigate = useNavigate();

  if (!product) {
    return (
      <div className="product-not-found">
        <div className="error-icon">⚠️</div>
        <h2>Product Not Found</h2>
        <p>We couldn't find the product you're looking for.</p>
        <Link to="/" className="back-to-shop">
          ← Back to Shop
        </Link>
      </div>
    );
  }

  const handleAddToCart = async () => {
    // Check authentication first
    const authCheck = checkAuthBeforeCartAction();
    if (!authCheck.allowed) {
      showToast(authCheck.message, 'error');
      navigate('/login', { state: { from: `/product/${product.id}` } });
      return;
    }

    let hasError = false;
    
    if (product.sizes && !selectedSize) {
      setSizeError(true);
      hasError = true;
      showToast('Please select a size', 'error');
    }

    if (product.colors && !selectedColor) {
      setColorError(true);
      hasError = true;
      showToast('Please select a color', 'error');
    }

    if (hasError) return;

    setIsAddingToCart(true);
    
    try {
      const added = await addToCart({
        ...product,
        quantity,
        selectedSize,
        selectedColor
      });
      
      if (!added) {
        showToast('Failed to add item to cart', 'error');
      }
    } catch (error) {
      showToast('An error occurred while adding to cart', 'error');
    } finally {
      setIsAddingToCart(false);
    }
  };

  const discount = product.oldPrice && product.newPrice
    ? Math.round(((product.oldPrice - product.newPrice) / product.oldPrice) * 100)
    : 0;

  const compositeId = `${product.id}-${selectedSize}-${selectedColor}`;
  const cartQuantity = cartItems[compositeId] || 0;
  const isLoggedIn = localStorage.getItem('auth-token') !== null;

  // Handle multiple images if available
  const images = product.images?.length > 0 ? product.images : [product.image];
  const additionalImages = images.slice(1);

  return (
    <div className="product-display">
      <div className="product-images">
        <div className="main-image-container">
          <img
            src={images[activeImage]}
            alt={product.name}
            className="main-image"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = '/images/default-product.jpg';
            }}
          />
          {discount > 0 && (
            <div className="discount-badge">
              {discount}% OFF
            </div>
          )}
        </div>

        {additionalImages.length > 0 && (
          <div className="thumbnail-container">
            {additionalImages.map((img, index) => (
              <div 
                key={index}
                className={`thumbnail ${activeImage === index + 1 ? 'active' : ''}`}
                onClick={() => setActiveImage(index + 1)}
              >
                <img src={img} alt={`${product.name} thumbnail ${index + 1}`} />
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="product-details">
        <div className="product-header">
          <h1>{product.name}</h1>
          {product.category && (
            <p className="category">
              Category: <span>{product.category}</span>
            </p>
          )}
          {product.brand && (
            <p className="brand">
              Brand: <span>{product.brand}</span>
            </p>
          )}
        </div>

        <div className="pricing">
          <div className="price-container">
            {product.oldPrice && (
              <span className="old-price">Ksh. {product.oldPrice.toFixed(2)}</span>
            )}
            <span className="current-price">Ksh. {product.newPrice.toFixed(2)}</span>
            {discount > 0 && (
              <span className="discount-save">
                You save Ksh. {(product.oldPrice - product.newPrice).toFixed(2)}
              </span>
            )}
          </div>
          {product.rating && (
            <div className="rating">
              {[...Array(5)].map((_, i) => (
                <span 
                  key={i} 
                  className={`star ${i < Math.floor(product.rating) ? 'filled' : ''}`}
                >
                  {i < product.rating ? '★' : '☆'}
                </span>
              ))}
              <span className="review-count">({product.reviewCount || 0} reviews)</span>
            </div>
          )}
        </div>

        {product.description && (
          <div className="description-section">
            <h3>Description</h3>
            <p>{product.description}</p>
          </div>
        )}

        {product.sizes && (
          <div className={`size-selector ${sizeError ? 'error' : ''}`}>
            <h3>
              Size {sizeError && <span className="error-text">(Required)</span>}
            </h3>
            <div className="sizes">
              {product.sizes.map(size => (
                <button
                  key={size}
                  className={`size-btn ${selectedSize === size ? 'active' : ''}`}
                  onClick={() => {
                    setSelectedSize(size);
                    setSizeError(false);
                  }}
                >
                  {size}
                </button>
              ))}
            </div>
          </div>
        )}

        {product.colors && (
          <div className={`color-selector ${colorError ? 'error' : ''}`}>
            <h3>
              Color {colorError && <span className="error-text">(Required)</span>}
            </h3>
            <div className="colors">
              {product.colors.map(color => (
                <div
                  key={color}
                  className={`color-option ${selectedColor === color ? 'active' : ''}`}
                  style={{ backgroundColor: color }}
                  onClick={() => {
                    setSelectedColor(color);
                    setColorError(false);
                  }}
                  title={color}
                >
                  {selectedColor === color && (
                    <span className="checkmark">✓</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="quantity-selector">
          <h3>Quantity</h3>
          <div className="quantity-controls">
            <button 
              onClick={() => setQuantity(q => Math.max(1, q - 1))}
              className="quantity-btn"
              disabled={quantity <= 1}
            >
              −
            </button>
            <span className="quantity-display">{quantity}</span>
            <button 
              onClick={() => setQuantity(q => q + 1)}
              className="quantity-btn"
            >
              +
            </button>
          </div>
        </div>

        <button
          className={`add-to-cart ${isAddingToCart ? 'adding' : ''} ${!isLoggedIn ? 'login-prompt' : ''}`}
          onClick={handleAddToCart}
          disabled={
            isAddingToCart ||
            (product.sizes && !selectedSize) || 
            (product.colors && !selectedColor)
          }
        >
          {isAddingToCart ? (
            <span className="adding-text">Adding...</span>
          ) : isLoggedIn ? (
            cartQuantity > 0 ? `Add More (${cartQuantity} in cart)` : 'Add to Cart'
          ) : (
            'Login to Add to Cart'
          )}
          {isAddingToCart && <span className="spinner"></span>}
        </button>

        {!isLoggedIn && (
          <div className="login-reminder">
            <p>You need to login to add items to your cart.</p>
            <Link to="/login" className="login-link">Login or Create Account</Link>
          </div>
        )}

        {product.specifications && (
          <div className="specifications">
            <h3>Specifications</h3>
            <ul>
              {Object.entries(product.specifications).map(([key, value]) => (
                <li key={key}>
                  <strong>{key}:</strong> {value}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDisplay;